import { useMemo } from 'react';
import { useAuthStore, useDownloadStatementStore } from '@lib/store';
import { AmountUtils, CardImageUtils, cn, FormatUtils } from '@shared/utils';
import type { GetCardsResponse } from '@shared/api';
import { Button } from '@shared/widgets';
import InvoiceDueIcon from '@assets/icons/invoiceDue.svg';
import WarningIcon from '@assets/icons/warning.svg';

interface CardSlideItemProps {
  card: GetCardsResponse;
  selectedCard?: GetCardsResponse;
  isVisibleDownloadButton?: boolean;
  onShowTransferToCardModal: () => void;
  onSelectedCard: (card: GetCardsResponse | undefined) => void;
}

export const CardSlideItem = (props: CardSlideItemProps) => {
  const authStore = useAuthStore();
  const downloadStatementStore = useDownloadStatementStore();

  const topUpHandler = () => {
    props.onSelectedCard(props.card);
    props.onShowTransferToCardModal();
  };
  const downloadHandler = () => props.card.InvoiceDate && downloadStatementStore.downloadStatement(props.card.Id, authStore.data!.token);

  const images = useMemo(() => CardImageUtils.getCardImages(props.card.Product), [props.card.Product]);
  return (
    <>
      <div className='absolute top-0 left-0 h-full w-full rotate-y-0 duration-300 backface-hidden'>
        {images?.front}
        <div className='absolute inset-0 [margin:5%_5%_8%_7%] min-h-[253px] font-[Share_Tech_Mono] text-[18px] text-[rgba(88,88,88,0.6)] uppercase [text-shadow:-1px_-1px_0_#fff,1px_0px_0_rgba(0,0,0,0.4),1px_-1px_2px_rgba(255,255,255,0.4),-2px_-1px_1px_rgba(0,0,0,0.4),-2px_0px_2px_rgba(0,0,0,0.25)]'>
          <div className='absolute top-1 right-1 text-2xl not-lg:text-xl'>{props.card.Currency}</div>
          <div className='absolute top-[45%] right-[40%] left-5 items-center justify-between text-[26px] whitespace-nowrap max-sm:text-[21px]'>
            {FormatUtils.formatCardNumber(props.card.Number)}
          </div>
          <div className='absolute top-[60%] left-0'>{props.card.CardHolder}</div>
        </div>
        {props.card.InvoiceDate != '' && (
          <div className='absolute top-0 left-[33%] h-[23px] w-[143px] text-center uppercase'>
            <InvoiceDueIcon />
            <span className='absolute top-0 right-0 left-0 text-sm font-semibold text-white'>Invoice Due</span>
          </div>
        )}
      </div>
      <div className='absolute top-0 left-0 z-2 h-full w-full rotate-y-180 duration-300 backface-hidden'>
        {images?.back}
        <div
          className={cn(
            'absolute top-0 left-0 flex min-h-[77px] w-full flex-row items-center rounded-t-[6px] bg-gradient-to-b from-[#3b3b3b] to-[#373737] p-[8px_9px_8px_16px] shadow-[2px_2px_4px_0_#0000003b] [box-shadow:inset_0_1px_0_0_#4b4b4b]',
            props.card.InvoiceDate != '' ? '' : 'justify-end',
          )}
        >
          {props.card.InvoiceDate != '' ? (
            <>
              <WarningIcon className='h-[70px] w-5 min-w-5 bg-contain bg-top bg-no-repeat' />
              <div className='flex h-full w-full flex-col justify-between pl-2.5'>
                <div className='text-left text-sm text-[#a8a8a8]'>Outstanding Balance</div>
                <div className='text-start text-[15px] text-[#f2f2f2] uppercase'>
                  {AmountUtils.transformToAmount(props.card.InvoiceAmount)} {props.card.Currency}
                </div>
                <div className='text-left text-sm text-[#a8a8a8]'>
                  Settlement Date : <div>{props.card.InvoiceDate}</div>
                </div>
              </div>

              <div className='w-full max-w-[150px]'>
                <Button className='px-2' onClick={topUpHandler}>
                  Make a payment
                </Button>
              </div>
            </>
          ) : (
            <div className='w-full max-w-[150px]'>
              <Button className='px-2' onClick={topUpHandler}>
                Transfer to card
              </Button>
            </div>
          )}
        </div>
        {props.isVisibleDownloadButton && props.card.InvoiceDate && (
          <div className='mt-4 flex flex-col items-start justify-start'>
            <div className='mt-[26px] flex w-full items-center justify-start'>
              <div className='flex items-center justify-start' onClick={(e) => (e.stopPropagation(), downloadHandler())}>
                <div className='rotate-90'>
                  <svg width='24' height='20' viewBox='0 0 24 20' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M16.097 10.643l-4.242 4.242c-.355.355-.931.355-1.286 0s-.355-.931 0-1.286l2.69-2.69h-12.351c-.502 0-.909-.407-.909-.909s.407-.909.909-.909h12.351l-2.69-2.691c-.355-.355-.355-.931 0-1.286s.931-.355 1.286 0l4.242 4.243.003.003.058.064.024.032.029.039.024.039.022.037.02.041.019.04.015.04.015.044.01.041.011.046.007.048.006.04.005.09-.005.09-.006.04-.007.048-.011.045-.011.042-.015.043-.015.041-.018.039-.02.041-.022.037-.024.039-.029.04-.024.032-.06.067zm6.085-9.734c0-.502.407-.909.909-.909s.909.407.909.909v18.182c0 .502-.407.909-.909.909s-.909-.407-.909-.909v-18.182z'
                      fill='#CDAF80'
                    />
                  </svg>
                </div>
                <div className='ml-4 text-center text-sm font-semibold text-[#CDAF80] uppercase'>Download Statement</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
